import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye, faChartPie, faUpDown, faArrowTrendUp } from '@fortawesome/free-solid-svg-icons';
import Modal from '@/components/ui/Modal';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';

interface SignalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SignalModal({ isOpen, onClose }: SignalModalProps) {
  const { hasFeature } = useFeatureAccess();

  const allCategories = [
    {
      name: 'Kernstrategie',
      href: '/signale/kernstrategie',
      icon: faBullseye,
      description: 'Grundlegende Trading-Strategie',
      color: 'blue',
      feature: 'coreRecommendations' as const
    },
    {
      name: 'Earnings Trades',
      href: '/signale/earning-trades',
      icon: faChartPie,
      description: 'Spezialisierte Strategien für Earnings-Events',
      color: 'green',
      feature: 'earningRecommendations' as const
    },
    {
      name: 'Swing Trades',
      href: '/signale/swing-trades',
      icon: faUpDown,
      description: 'Mittelfristige Trading-Strategien',
      color: 'purple',
      feature: 'swingTradeRecommendations' as const
    },
    {
      name: 'Langzeit Trades',
      href: '/signale/langzeit-trades',
      icon: faArrowTrendUp,
      description: 'Langfristige Trading-Strategien',
      color: 'orange',
      feature: 'longTermRecommendations' as const
    }
  ];

  // Filter categories based on user's feature access
  const categories = allCategories.filter(category => hasFeature(category.feature));

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      green: 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400',
      purple: 'bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      orange: 'bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-600 dark:text-orange-400'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Signal Kategorien"
      maxWidth="lg"
    >
      <div className="p-6">
        {categories.length > 0 ? (
          <>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Wählen Sie eine Signal-Kategorie:
            </p>

            <div className="grid grid-cols-1 gap-4">
              {categories.map((category) => (
                <Link
                  key={category.name}
                  to={category.href}
                  onClick={onClose}
                  className={`block p-6 rounded-lg transition-colors cursor-pointer ${getColorClasses(category.color)}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      category.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/50' :
                      category.color === 'green' ? 'bg-green-100 dark:bg-green-900/50' :
                      category.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/50' :
                      'bg-orange-100 dark:bg-orange-900/50'
                    }`}>
                      <FontAwesomeIcon icon={category.icon} className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                        {category.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              Keine Signal-Kategorien verfügbar. Bitte wenden Sie sich an Ihren Administrator.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
